// XiaoyuClaw Apps API client for chat-ui.
//
// Apps live in the Controller (not SkillHub) and are authenticated with the
// same ruoyi-ai bearer token stored in the user pinia store.

import type {
  App,
  AppDetailResponse,
  AppFileContentResponse,
  AppFileListResponse,
  AppListResponse,
  AppPage,
  AppsEnvelopeResponse,
  AppServeToken,
  AppUploadLink,
  AppUploadResponse,
  AppVersionListResponse,
  CreateAppRequest,
  CreatePageRequest,
  UpdateAppRequest,
  UpdatePageRequest,
} from './types';
import { useUserStore } from '@/stores';
import { getHttpStatusTip } from '@/utils/httpErrorMessage';

const CONTROLLER_URL = import.meta.env.VITE_CONTROLLER_URL || '/controller';

// Dev per-app preview: the Traefik origin publishes on a fixed host port
// (compose `${TRAEFIK_APP_PORT:-9081}:80`) and the browser reaches it directly
// — no edge proxy in dev — so the derived preview origin must re-attach that
// port (window.location.host would carry the Vite port 5173). Prod has an edge
// nginx on 443, so window.location.host (no port) is correct there.
const DEV_APP_PREVIEW_PORT = 9081;

function resolveUrl(base: string, path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!base)
    return normalizedPath;
  return `${base.replace(/\/$/, '')}${normalizedPath}`;
}

async function unwrap<T>(response: Response): Promise<T> {
  if (response.status === 401)
    throw new Error('unauthorized');

  let body: AppsEnvelopeResponse<T> | null = null;
  try {
    body = (await response.json()) as AppsEnvelopeResponse<T>;
  }
  catch {
    throw new Error(response.statusText || 'invalid response');
  }

  if (!body || !body.success)
    throw new Error(body?.error || `Request failed: ${response.statusText}`);

  return body.data as T;
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = useUserStore().token;
  if (token)
    headers.Authorization = `Bearer ${token}`;
  return headers;
}

function jsonHeaders(): Record<string, string> {
  return authHeaders({ 'Content-Type': 'application/json' });
}

async function handleError(response: Response): Promise<Error> {
  const tip = await getHttpStatusTip(response.status);
  return new Error(tip || `Request failed: ${response.statusText}`);
}

// --- Personal apps ---

export async function listMyApps(): Promise<App[]> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/instances/me/apps'), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  const data = await unwrap<AppListResponse>(res);
  return data.apps || [];
}

export async function createApp(req: CreateAppRequest): Promise<AppDetailResponse> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/instances/me/apps'), {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(req),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppDetailResponse>(res);
}

export async function getApp(id: string): Promise<AppDetailResponse> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(id)}`), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppDetailResponse>(res);
}

export async function updateApp(id: string, req: UpdateAppRequest): Promise<AppDetailResponse> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(id)}`), {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(req),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppDetailResponse>(res);
}

export async function deleteApp(id: string): Promise<void> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(id)}`), {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
}

export async function shareApp(id: string): Promise<App> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(id)}/share`), {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<App>(res);
}

// --- Pages ---

export async function listPages(appId: string): Promise<AppPage[]> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/pages`), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  const data = await unwrap<{ pages: AppPage[] }>(res);
  return data.pages || [];
}

export async function createPage(appId: string, req: CreatePageRequest): Promise<AppPage> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/pages`), {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(req),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppPage>(res);
}

export async function updatePage(appId: string, pageId: string, req: UpdatePageRequest): Promise<AppPage> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/pages/${encodeURIComponent(pageId)}`),
    {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(req),
    },
  );
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppPage>(res);
}

export async function updatePageContent(appId: string, pageId: string, content: string): Promise<AppPage> {
  return updatePage(appId, pageId, { content });
}

export async function deletePage(appId: string, pageId: string): Promise<void> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/pages/${encodeURIComponent(pageId)}`),
    {
      method: 'DELETE',
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw await handleError(res);
}

// --- Org catalog ---

export async function listOrgApps(): Promise<App[]> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/apps'), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  const data = await unwrap<AppListResponse>(res);
  return data.apps || [];
}

export async function getOrgApp(id: string): Promise<AppDetailResponse> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/apps/${encodeURIComponent(id)}`), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppDetailResponse>(res);
}

// --- Packaged micro-apps ---

/**
 * Upload a zip-packaged micro-app from the browser (ruoyi bearer). Pass appId to
 * add a new version to an existing owned package app; omit it to create one.
 */
export async function uploadAppPackage(file: File, appId?: string): Promise<AppUploadResponse> {
  const form = new FormData();
  form.append('package', file);
  if (appId)
    form.append('app_id', appId);
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/instances/me/apps/upload'), {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppUploadResponse>(res);
}

/**
 * Mint a short-lived Agent upload link (xclaw_au_) bound to the caller. The
 * link carries its own ~1h expiry; each call issues a fresh link. Copy it and
 * hand it to an external agent, which POSTs a zip to the link as the credential.
 */
export async function generateUploadLink(): Promise<AppUploadLink> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/instances/me/apps/upload-link'), {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppUploadLink>(res);
}

/**
 * Mint a short-lived Agent upload link (xclaw_au_) bound to the caller AND scoped
 * to a single owned package app. Unlike generateUploadLink — whose link lets an
 * agent target any of the caller's apps or create a new one — this link always
 * uploads a NEW VERSION of `appId`: the :id rides in the path so the target app
 * is fixed, and the bundle's app.json overrides the app's name/description. Copy
 * it and hand it to an external agent, which POSTs a zip to the link as the credential.
 */
export async function generateAppUploadLink(appId: string): Promise<AppUploadLink> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/upload-link`), {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppUploadLink>(res);
}

/**
 * Mint a short-lived serving token so a sandboxed iframe can load a package
 * app's static assets (iframe subresource requests cannot carry an
 * Authorization header). Org+published apps are mintable by any logged-in user.
 */
export async function requestServeToken(appId: string): Promise<AppServeToken> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/apps/${encodeURIComponent(appId)}/serve-token`), {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppServeToken>(res);
}

/**
 * Build the iframe-loadable URL for a package app page, signed with a serving
 * token. The token rides in the PATH so a micro-app's relative asset references
 * (./app.js, ./style.css) inherit it automatically. Pass `path` (an in-bundle
 * HTML filename) to render a specific page; omit it for the entry/landing page.
 */
export function serveAppUrl(appId: string, serveToken: string, path?: string): string {
  const base = resolveUrl(CONTROLLER_URL, `/apps/${encodeURIComponent(appId)}/serve/${encodeURIComponent(serveToken)}/`);
  return path ? `${base}${encodeURIComponent(path)}` : base;
}

/**
 * Publish a Vibe Coding app (inline app_pages HTML) as a served package
 * micro-app through the same upload pipeline. The original Vibe app is kept.
 */
export async function publishAppAsPackage(id: string): Promise<AppUploadResponse> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(id)}/publish-package`), {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppUploadResponse>(res);
}

/**
 * Build an absolute, copy-pasteable upload URL from an Agent-upload-link token.
 * External agents POST a zip here (the token is the credential). Uses the
 * chat-ui origin so the URL works from outside the platform UI.
 */
export function buildAbsoluteUploadUrl(token: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${resolveUrl(CONTROLLER_URL, '/apps/upload')}?token=${encodeURIComponent(token)}`;
}

/**
 * Build an absolute, copy-pasteable PER-APP upload URL from a per-app
 * Agent-upload-link token. The :id rides in the path so the upload is forced to
 * that one app (a new version, never a different app or a new app). Uses the
 * chat-ui origin so the URL works from outside the platform UI.
 */
export function buildAbsoluteAppUploadUrl(appId: string, token: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${resolveUrl(CONTROLLER_URL, `/apps/${encodeURIComponent(appId)}/upload`)}?token=${encodeURIComponent(token)}`;
}

/** Download a package app's source zip (owner only) as a browser save. */
export async function downloadAppSource(appId: string): Promise<void> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/source`), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `${appId}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- Online file editing (D1) ---

function appFilesBase(appId: string): string {
  return resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/files`);
}

/** List all files in a package app's active bundle (flat, recursive). */
export async function listAppFiles(appId: string): Promise<AppFileListResponse> {
  const res = await fetch(appFilesBase(appId), { headers: authHeaders() });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppFileListResponse>(res);
}

/** Read a text file's content for the editor. */
export async function getAppFileContent(appId: string, path: string): Promise<AppFileContentResponse> {
  const res = await fetch(`${appFilesBase(appId)}/content?path=${encodeURIComponent(path)}`, {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppFileContentResponse>(res);
}

/** Write (create or overwrite) a text file in the bundle. */
export async function putAppFileContent(appId: string, path: string, content: string): Promise<void> {
  const res = await fetch(`${appFilesBase(appId)}/content?path=${encodeURIComponent(path)}`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify({ content }),
  });
  if (!res.ok)
    throw await handleError(res);
}

/** Create a directory in the bundle. */
export async function mkdirAppFile(appId: string, path: string): Promise<void> {
  const res = await fetch(`${appFilesBase(appId)}/mkdir`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ path }),
  });
  if (!res.ok)
    throw await handleError(res);
}

/** Delete a file or directory from the bundle. */
export async function deleteAppFile(appId: string, path: string): Promise<void> {
  const res = await fetch(`${appFilesBase(appId)}?path=${encodeURIComponent(path)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
}

/** Rename/move a file or directory. */
export async function renameAppFile(appId: string, from: string, to: string): Promise<void> {
  const res = await fetch(`${appFilesBase(appId)}/rename`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ from, to }),
  });
  if (!res.ok)
    throw await handleError(res);
}

/** List all versions of a package app plus the active version id. */
export async function listAppVersions(appId: string): Promise<AppVersionListResponse> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/versions`), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<AppVersionListResponse>(res);
}

/** Snapshot the current bundle as a new version (optional label). */
export async function snapshotAppVersion(appId: string, version?: string): Promise<{ version: string; version_id: string }> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/versions`), {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(version ? { version } : {}),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<{ version: string; version_id: string }>(res);
}

/** Roll back to a prior version. */
export async function setActiveAppVersion(appId: string, versionId: string): Promise<void> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/versions/active`), {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify({ version_id: versionId }),
  });
  if (!res.ok)
    throw await handleError(res);
}

/** Sync the agent's workspace-authored files (workspace/apps/<appId>/) into the bundle. */
export async function syncAppWorkspace(appId: string): Promise<{ synced: boolean; message?: string }> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/sync`), {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<{ synced: boolean; message?: string }>(res);
}

// --- Lightweight runtime container (D5) ---

/** Start (or re-run) an app's lightweight run container: build + serve. */
export async function appRuntimeEnsure(appId: string): Promise<{ app_id: string; status: string; version: string }> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/runtime/ensure`), {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<{ app_id: string; status: string; version: string }>(res);
}

/** Stop an app's run container. */
export async function appRuntimeStop(appId: string): Promise<void> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/runtime/stop`), {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
}

/** Fetch the live container status of an app's run container. */
export async function appRuntimeStatus(appId: string): Promise<{ app_id: string; status: string; exit_code: number; port: number; version: string }> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/runtime/status`), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<{ app_id: string; status: string; exit_code: number; port: number; version: string }>(res);
}

/**
 * Build the iframe-loadable URL for a running app's serve port, signed with a
 * serving token. No build-time config — one chat-ui bundle serves every
 * deployment environment. Both modes derive the app's own origin at runtime as
 * `<page-proto>//app-<id>.<host>/?t=<token>` (Traefik routes it to the container
 * with no rewriting; the `?t=` token bootstraps the auth cookie via Controller
 * forward-auth; scheme follows the host page so https chat-ui never mixes content):
 *
 *  - Production: host = window.location.host (e.g. ai.blmdigital.cn, no port —
 *    the edge nginx serves 443 and forwards to Traefik). Each environment only
 *    needs its own `*.<host>` wildcard DNS + cert.
 *  - Dev: host = <window.location.hostname>:9081 — the browser reaches the
 *    per-app Traefik origin directly on its published port (no edge proxy in
 *    dev), so the port must be re-attached (window.location.host would carry the
 *    Vite port 5173). `*.localhost` resolves to 127.0.0.1.
 */
export function appRuntimeUrl(appId: string, serveToken: string): string {
  const proto = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  let host = typeof window !== 'undefined' ? window.location.host : '';
  if (import.meta.env.DEV) {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    host = `${hostname}:${DEV_APP_PREVIEW_PORT}`;
  }
  return `${proto}//app-${appId}.${host}/?t=${encodeURIComponent(serveToken)}`;
}

/**
 * Fetch the run container's combined build/serve log tail. Split client-side at
 * the entrypoint's "[app-runtime] starting:" marker into build vs runtime logs.
 */
export async function appRuntimeLogs(appId: string, tail = 400): Promise<string> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, `/instances/me/apps/${encodeURIComponent(appId)}/runtime/logs?tail=${tail}`), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  const data = await unwrap<{ app_id: string; logs: string }>(res);
  return data?.logs ?? '';
}

// --- Current user identity ---

/**
 * Response of `GET /auth/me` on the Controller. user_id is a string (the
 * stringified YY Long) so it can be compared against App.owner_user_id without
 * losing precision on 19-digit ids.
 */
export interface MeResponse {
  user_id: string;
  user_name: string;
  is_admin: boolean;
  roles?: Array<{ roleKey: string; roleName: string }>;
}

/**
 * Resolve the calling user's identity + admin flag from the Controller's
 * validated ruoyi token. Used to gate admin-only UI (e.g. running an approved
 * enterprise runtime app) without depending on the YY SSO response shape.
 */
export async function getMe(): Promise<MeResponse> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/auth/me'), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw await handleError(res);
  return unwrap<MeResponse>(res);
}
